import { validateSymbolReferences } from '../../symbols/registry';
import { SymbolImportError } from '../../symbols/model';
import { createAnnotation, createAnnotationLayer } from '../../annotations/defaults';
import { appendDistinctCoordinate, transformAnnotationForDrag } from '../../annotations/geometry';
import {
  annotationLayerLegendEligibility,
  reconcileAnnotationLayerLegend,
} from '../../annotations/legend';
import { ANNOTATION_LIMITS } from '../../annotations/model';
import { AnnotationValidationError, parseAnnotation } from '../../annotations/validation';
import { isTableSource } from '../../data/model';
import { generateSelectionArea, prepareAreaSelection } from '../../selection/createArea';
import { isCapabilityAvailable } from '../../licensing/entitlements';
import { createEmptyProject } from '../../project/model';
import { ACTION_OK, actionError, type AppStoreActionContext } from './actionSupport';
import { DEFAULT_SHELL_UI_STATE, type AppState } from './appState';

export type AnnotationActions = Pick<
  AppState,
  | 'addAnnotationLayer'
  | 'createAreaFromSelection'
  | 'removeAnnotationLayer'
  | 'renameAnnotationLayer'
  | 'reorderAnnotationLayer'
  | 'selectAnnotationLayer'
  | 'toggleAnnotationLayerVisibility'
  | 'setAnnotationLayerLegend'
  | 'startAnnotationDrawing'
  | 'placeAnnotationCoordinate'
  | 'finishAnnotationDrawing'
  | 'cancelAnnotationDrawing'
  | 'selectAnnotation'
  | 'updateAnnotation'
  | 'moveAnnotation'
  | 'moveAnnotationToLayer'
  | 'moveAnnotationToNewLayer'
  | 'removeAnnotation'
  | 'setActiveFocusArea'
>;

export function createAnnotationActions({ get, set }: AppStoreActionContext): AnnotationActions {
  return {
    createAreaFromSelection: (features, output, bufferMetres) => {
      const state = get();
      if (
        state.mode !== 'editor' ||
        !isCapabilityAvailable(state.entitlement, 'annotations.author')
      )
        return actionError('Annotation authoring requires Pro in the Editor.');
      const layers = state.project.annotationLayers;
      const existing = layers.find((layer) => layer.id === state.shell.activeAnnotationLayerId);
      if (!existing && layers.length >= ANNOTATION_LIMITS.layersPerProject)
        return actionError('This project has reached the annotation-layer safety limit.');
      if (
        output === 'focus-area' &&
        existing?.annotations.some((item) => item.type === 'focus-area')
      )
        return actionError(
          'This annotation layer already has a Focus Area. Select another annotation layer.',
        );
      if (
        (existing?.annotations.length ?? 0) >= ANNOTATION_LIMITS.annotationsPerLayer ||
        layers.reduce((count, candidate) => count + candidate.annotations.length, 0) >=
          ANNOTATION_LIMITS.annotationsPerProject
      )
        return actionError('This project has reached the annotation safety limit.');
      try {
        const layer = existing ?? createAnnotationLayer(layers);
        const geometry = generateSelectionArea(prepareAreaSelection(features), bufferMetres);
        const parts = (
          geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates
        ).map((rings) => {
          const coordinates = rings.map((ring) =>
            ring.map(([x, y]): readonly [number, number] => [x!, y!]),
          );
          return {
            coordinates: coordinates[0]!,
            ...(coordinates.length > 1 ? { holes: coordinates.slice(1) } : {}),
          };
        });
        const annotation = parseAnnotation({
          ...createAnnotation(layer, output, parts[0]!.coordinates),
          ...parts[0],
          ...(parts.length > 1 ? { additionalParts: parts.slice(1) } : {}),
        });
        const updated = reconcileAnnotationLayerLegend({
          ...layer,
          annotations: [...layer.annotations, annotation],
        });
        set({
          project: {
            ...state.project,
            annotationLayers: existing
              ? layers.map((candidate) => (candidate.id === layer.id ? updated : candidate))
              : [...layers, updated],
            activeFocusAreaId:
              output === 'focus-area' && state.project.activeFocusAreaId === null
                ? annotation.id
                : state.project.activeFocusAreaId,
          },
          shell: {
            ...state.shell,
            activeAnnotationLayerId: layer.id,
            selectedAnnotationId: annotation.id,
            selectedLayerId: null,
            selectedTableSourceId: null,
            inspectedFeature: null,
            inspectorOpen: true,
            annotationCreationTool: null,
            annotationDraftCoordinates: [],
            replacingFocusAreaId: null,
          },
        });
        return ACTION_OK;
      } catch (error) {
        return actionError(error instanceof Error ? error.message : 'Unable to generate an area.');
      }
    },
    addAnnotationLayer: () => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author')) {
        return actionError('Annotation authoring requires Pro.');
      }
      if (state.project.annotationLayers.length >= ANNOTATION_LIMITS.layersPerProject) {
        return actionError('This project has reached the annotation-layer safety limit.');
      }
      const layer = createAnnotationLayer(state.project.annotationLayers);
      set({
        mode: state.mode === 'empty' ? 'editor' : state.mode,
        project: {
          ...state.project,
          annotationLayers: [...state.project.annotationLayers, layer],
        },
        shell: {
          ...state.shell,
          selectedLayerId: null,
          selectedTableSourceId: null,
          activeAnnotationLayerId: layer.id,
          selectedAnnotationId: null,
          annotationCreationTool: null,
          annotationDraftCoordinates: [],
          inspectorOpen: true,
        },
      });
      return ACTION_OK;
    },
    removeAnnotationLayer: (layerId) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      const annotationLayers = state.project.annotationLayers.filter(
        (layer) => layer.id !== layerId,
      );
      const removedLayer = state.project.annotationLayers.find((layer) => layer.id === layerId);
      const removesActiveFocus = removedLayer?.annotations.some(
        (annotation) => annotation.id === state.project.activeFocusAreaId,
      );
      if (annotationLayers.length === state.project.annotationLayers.length)
        return actionError('The annotation layer is unavailable.');
      const projectIsEmpty =
        annotationLayers.length === 0 &&
        state.project.symbolAssets.length === 0 &&
        state.project.layers.length === 0 &&
        state.project.sources.filter(isTableSource).length === 0;
      if (projectIsEmpty) {
        set({ mode: 'empty', project: createEmptyProject(), shell: DEFAULT_SHELL_UI_STATE });
        return ACTION_OK;
      }
      const fallback = annotationLayers[0]?.id ?? null;
      const { [layerId]: _removedLegendEntries, ...remainingLegendEntries } =
        state.project.composition.legend.excludedEntryKeysByLayer;
      void _removedLegendEntries;
      set({
        project: {
          ...state.project,
          annotationLayers,
          activeFocusAreaId: removesActiveFocus ? null : state.project.activeFocusAreaId,
          composition: {
            ...state.project.composition,
            legend: {
              ...state.project.composition.legend,
              excludedLayerIds: state.project.composition.legend.excludedLayerIds.filter(
                (id) => id !== layerId,
              ),
              hiddenGroupHeadingIds: state.project.composition.legend.hiddenGroupHeadingIds.filter(
                (id) => id !== layerId,
              ),
              excludedEntryKeysByLayer: remainingLegendEntries,
            },
          },
        },
        shell: {
          ...state.shell,
          activeAnnotationLayerId:
            state.shell.activeAnnotationLayerId === layerId
              ? fallback
              : state.shell.activeAnnotationLayerId,
          selectedAnnotationId:
            state.shell.activeAnnotationLayerId === layerId
              ? null
              : state.shell.selectedAnnotationId,
          annotationCreationTool: null,
          annotationDraftCoordinates: [],
          inspectorOpen:
            state.shell.activeAnnotationLayerId === layerId
              ? fallback !== null
              : state.shell.inspectorOpen,
        },
      });
      return ACTION_OK;
    },
    renameAnnotationLayer: (layerId, name) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      const normalised = name.trim();
      if (!normalised || normalised.length > ANNOTATION_LIMITS.nameLength)
        return actionError(
          `Annotation-layer names must contain 1 to ${ANNOTATION_LIMITS.nameLength} characters.`,
        );
      if (!state.project.annotationLayers.some((layer) => layer.id === layerId))
        return actionError('The annotation layer is unavailable.');
      set({
        project: {
          ...state.project,
          annotationLayers: state.project.annotationLayers.map((layer) =>
            layer.id === layerId ? { ...layer, name: normalised } : layer,
          ),
        },
      });
      return ACTION_OK;
    },
    reorderAnnotationLayer: (layerId, targetIndex) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      const currentIndex = state.project.annotationLayers.findIndex(
        (layer) => layer.id === layerId,
      );
      if (
        currentIndex < 0 ||
        targetIndex < 0 ||
        targetIndex >= state.project.annotationLayers.length ||
        currentIndex === targetIndex
      )
        return actionError('The annotation layer cannot be moved there.');
      const next = [...state.project.annotationLayers];
      const [layer] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, layer!);
      set({ project: { ...state.project, annotationLayers: next } });
      return ACTION_OK;
    },
    selectAnnotationLayer: (layerId) =>
      set((state) => {
        if (!state.project.annotationLayers.some((layer) => layer.id === layerId)) return state;
        return {
          shell: {
            ...state.shell,
            selectedLayerId: null,
            selectedTableSourceId: null,
            activeAnnotationLayerId: layerId,
            selectedAnnotationId: null,
            annotationCreationTool: null,
            annotationDraftCoordinates: [],
            inspectorOpen:
              state.mode === 'editor' || state.mode === 'composition'
                ? true
                : state.shell.inspectorOpen,
          },
        };
      }),
    toggleAnnotationLayerVisibility: (layerId) =>
      set((state) => {
        const target = state.project.annotationLayers.find((layer) => layer.id === layerId);
        if (!target) return state;
        const becomingVisible = !target.visible;
        return {
          project: {
            ...state.project,
            annotationLayers: state.project.annotationLayers.map((layer) =>
              layer.id === layerId ? { ...layer, visible: becomingVisible } : layer,
            ),
          },
        };
      }),
    setAnnotationLayerLegend: (layerId, includeInLegend, legendLabel) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      const layer = state.project.annotationLayers.find((candidate) => candidate.id === layerId);
      if (!layer) return actionError('The annotation layer is unavailable.');
      const trimmedLabel = legendLabel?.trim();
      if (legendLabel && legendLabel.length > ANNOTATION_LIMITS.nameLength)
        return actionError(
          `Legend labels must contain at most ${ANNOTATION_LIMITS.nameLength} characters.`,
        );
      if (includeInLegend && !annotationLayerLegendEligibility(layer).eligible)
        return actionError('This annotation layer does not have one consistent legend symbol.');
      set({
        project: {
          ...state.project,
          annotationLayers: state.project.annotationLayers.map((candidate) =>
            candidate.id === layerId
              ? (() => {
                  const {
                    includeInLegend: _includeInLegend,
                    legendLabel: _legendLabel,
                    ...rest
                  } = candidate;
                  void _includeInLegend;
                  void _legendLabel;
                  return {
                    ...rest,
                    ...(includeInLegend ? { includeInLegend: true } : {}),
                    ...(trimmedLabel ? { legendLabel: trimmedLabel } : {}),
                  };
                })()
              : candidate,
          ),
        },
      });
      return ACTION_OK;
    },
    startAnnotationDrawing: (tool, replacingFocusAreaId = null) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      if (
        !state.shell.activeAnnotationLayerId ||
        !state.project.annotationLayers.some(
          (layer) => layer.id === state.shell.activeAnnotationLayerId,
        )
      )
        return actionError('Select an annotation layer first.');
      const activeLayer = state.project.annotationLayers.find(
        (layer) => layer.id === state.shell.activeAnnotationLayerId,
      )!;
      const existingFocus = activeLayer.annotations.find(
        (annotation) => annotation.type === 'focus-area',
      );
      if (tool === 'focus-area' && existingFocus && existingFocus.id !== replacingFocusAreaId)
        return actionError('This annotation layer already has a Focus Area.');
      set({
        shell: {
          ...state.shell,
          annotationCreationTool: tool,
          annotationDraftCoordinates: [],
          replacingFocusAreaId: tool === 'focus-area' ? replacingFocusAreaId : null,
          selectedAnnotationId: null,
          inspectedFeature: null,
          selectionMethod: null,
          selectionToolActive: false,
        },
      });
      return ACTION_OK;
    },
    placeAnnotationCoordinate: (coordinate, finish = false) => {
      const state = get();
      const tool = state.shell.annotationCreationTool;
      const layerId = state.shell.activeAnnotationLayerId;
      if (!tool || !layerId || !isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('No annotation drawing tool is active.');
      const draft = appendDistinctCoordinate(state.shell.annotationDraftCoordinates, coordinate);
      const shouldFinish =
        tool === 'marker' ||
        tool === 'text' ||
        (tool === 'rectangle' && draft.length >= 2) ||
        (tool === 'ellipse' && draft.length >= 2) ||
        (tool === 'callout' && draft.length >= 2) ||
        (finish &&
          ((tool === 'line' && draft.length >= 2) ||
            ((tool === 'polygon' || tool === 'focus-area') && draft.length >= 3)));
      if (!shouldFinish) {
        set({ shell: { ...state.shell, annotationDraftCoordinates: draft } });
        return ACTION_OK;
      }
      const layer = state.project.annotationLayers.find((candidate) => candidate.id === layerId);
      if (!layer) return actionError('The active annotation layer is unavailable.');
      const total = state.project.annotationLayers.reduce(
        (count, candidate) => count + candidate.annotations.length,
        0,
      );
      if (
        layer.annotations.length >= ANNOTATION_LIMITS.annotationsPerLayer ||
        total >= ANNOTATION_LIMITS.annotationsPerProject
      )
        return actionError('This project has reached the annotation safety limit.');
      const annotation = createAnnotation(layer, tool, draft);
      const replacedFocusAreaId = tool === 'focus-area' ? state.shell.replacingFocusAreaId : null;
      const annotationLayers = state.project.annotationLayers.map((candidate) =>
        candidate.id === layerId
          ? reconcileAnnotationLayerLegend({
              ...candidate,
              annotations: candidate.annotations
                .filter((existing) => existing.id !== replacedFocusAreaId)
                .concat(annotation),
            })
          : candidate,
      );
      const activeFocusAreaId =
        tool !== 'focus-area'
          ? state.project.activeFocusAreaId
          : state.project.activeFocusAreaId === replacedFocusAreaId ||
              state.project.activeFocusAreaId === null
            ? annotation.id
            : state.project.activeFocusAreaId;
      set({
        project: { ...state.project, annotationLayers, activeFocusAreaId },
        shell: {
          ...state.shell,
          selectedAnnotationId: annotation.id,
          annotationCreationTool: null,
          annotationDraftCoordinates: [],
          replacingFocusAreaId: null,
          inspectorOpen: true,
        },
      });
      return ACTION_OK;
    },
    finishAnnotationDrawing: () => {
      const state = get();
      const coordinate = state.shell.annotationDraftCoordinates.at(-1);
      return coordinate
        ? get().placeAnnotationCoordinate(coordinate, true)
        : actionError('Add coordinates before finishing the annotation.');
    },
    cancelAnnotationDrawing: () =>
      set((state) => ({
        shell: {
          ...state.shell,
          annotationCreationTool: null,
          annotationDraftCoordinates: [],
          replacingFocusAreaId: null,
        },
      })),
    selectAnnotation: (layerId, annotationId) =>
      set((state) => {
        const layer = state.project.annotationLayers.find((candidate) => candidate.id === layerId);
        if (!layer?.annotations.some((annotation) => annotation.id === annotationId)) return state;
        return {
          shell: {
            ...state.shell,
            activeAnnotationLayerId: layerId,
            selectedAnnotationId: annotationId,
            selectedLayerId: null,
            selectedTableSourceId: null,
            inspectorOpen: state.mode === 'editor' || state.mode === 'composition',
            annotationCreationTool: null,
            annotationDraftCoordinates: [],
            inspectedFeature: null,
          },
        };
      }),
    updateAnnotation: (layerId, annotationId, update) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      const sourceLayer = state.project.annotationLayers.find((layer) => layer.id === layerId);
      const current = sourceLayer?.annotations.find((annotation) => annotation.id === annotationId);
      if (!sourceLayer || !current) return actionError('The annotation is unavailable.');
      const replacement = update(current);
      if (replacement.id !== current.id || replacement.type !== current.type)
        return actionError('Annotation identity and type cannot be changed.');
      try {
        parseAnnotation(replacement);
      } catch (error) {
        if (error instanceof AnnotationValidationError) return actionError(error.message);
        throw error;
      }
      const annotationLayers = state.project.annotationLayers.map((layer) =>
        reconcileAnnotationLayerLegend({
          ...layer,
          annotations: layer.annotations.map((annotation) => {
            if (annotation.id === annotationId) return replacement;
            return annotation;
          }),
        }),
      );
      const project = { ...state.project, annotationLayers };
      try {
        validateSymbolReferences(project);
      } catch (error) {
        if (error instanceof SymbolImportError) return actionError(error.message);
        throw error;
      }
      set({ project });
      return ACTION_OK;
    },
    moveAnnotation: (
      layerId,
      annotationId,
      longitudeDelta,
      latitudeDelta,
      part = 'object',
      widthDelta = 0,
      heightDelta = 0,
    ) =>
      get().updateAnnotation(layerId, annotationId, (annotation) =>
        transformAnnotationForDrag(
          annotation,
          part,
          longitudeDelta,
          latitudeDelta,
          widthDelta,
          heightDelta,
        ),
      ),
    moveAnnotationToLayer: (sourceLayerId, annotationId, targetLayerId) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      const source = state.project.annotationLayers.find((layer) => layer.id === sourceLayerId);
      const target = state.project.annotationLayers.find((layer) => layer.id === targetLayerId);
      const annotation = source?.annotations.find((item) => item.id === annotationId);
      if (!source || !target || !annotation)
        return actionError('The annotation layer is unavailable.');
      if (sourceLayerId === targetLayerId) return ACTION_OK;
      if (target.annotations.length >= ANNOTATION_LIMITS.annotationsPerLayer)
        return actionError('The target annotation layer has reached its safety limit.');
      if (
        annotation.type === 'focus-area' &&
        target.annotations.some((item) => item.type === 'focus-area')
      )
        return actionError('The target annotation layer already has a Focus Area.');
      const annotationLayers = state.project.annotationLayers.map((layer) => {
        if (layer.id === sourceLayerId)
          return reconcileAnnotationLayerLegend({
            ...layer,
            annotations: layer.annotations.filter((item) => item.id !== annotationId),
          });
        if (layer.id === targetLayerId)
          return reconcileAnnotationLayerLegend({
            ...layer,
            annotations: [...layer.annotations, annotation],
          });
        return layer;
      });
      set({
        project: { ...state.project, annotationLayers },
        shell: {
          ...state.shell,
          activeAnnotationLayerId: targetLayerId,
          selectedAnnotationId: annotationId,
          inspectorOpen: true,
        },
      });
      return ACTION_OK;
    },
    moveAnnotationToNewLayer: (sourceLayerId, annotationId) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      if (state.project.annotationLayers.length >= ANNOTATION_LIMITS.layersPerProject)
        return actionError('This project has reached the annotation-layer safety limit.');
      const source = state.project.annotationLayers.find((layer) => layer.id === sourceLayerId);
      const annotation = source?.annotations.find((item) => item.id === annotationId);
      if (!source || !annotation) return actionError('The annotation is unavailable.');
      const target = createAnnotationLayer(state.project.annotationLayers);
      const annotationLayers = state.project.annotationLayers
        .map((layer) =>
          layer.id === sourceLayerId
            ? reconcileAnnotationLayerLegend({
                ...layer,
                annotations: layer.annotations.filter((item) => item.id !== annotationId),
              })
            : layer,
        )
        .concat({ ...target, annotations: [annotation] });
      set({
        project: { ...state.project, annotationLayers },
        shell: {
          ...state.shell,
          activeAnnotationLayerId: target.id,
          selectedAnnotationId: annotationId,
          annotationCreationTool: null,
          annotationDraftCoordinates: [],
          inspectorOpen: true,
        },
      });
      return ACTION_OK;
    },
    removeAnnotation: (layerId, annotationId) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      const layer = state.project.annotationLayers.find((candidate) => candidate.id === layerId);
      if (!layer?.annotations.some((annotation) => annotation.id === annotationId))
        return actionError('The annotation is unavailable.');
      set({
        project: {
          ...state.project,
          activeFocusAreaId:
            state.project.activeFocusAreaId === annotationId
              ? null
              : state.project.activeFocusAreaId,
          annotationLayers: state.project.annotationLayers.map((candidate) =>
            candidate.id === layerId
              ? reconcileAnnotationLayerLegend({
                  ...candidate,
                  annotations: candidate.annotations.filter(
                    (annotation) => annotation.id !== annotationId,
                  ),
                })
              : candidate,
          ),
        },
        shell: {
          ...state.shell,
          selectedAnnotationId:
            state.shell.selectedAnnotationId === annotationId
              ? null
              : state.shell.selectedAnnotationId,
        },
      });
      return ACTION_OK;
    },
    setActiveFocusArea: (annotationId) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'annotations.author'))
        return actionError('Annotation authoring requires Pro.');
      if (
        annotationId !== null &&
        !state.project.annotationLayers.some((layer) =>
          layer.annotations.some(
            (annotation) => annotation.id === annotationId && annotation.type === 'focus-area',
          ),
        )
      )
        return actionError('The Focus Area is unavailable.');
      set({ project: { ...state.project, activeFocusAreaId: annotationId } });
      return ACTION_OK;
    },
  };
}
